import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DangerousOutlinedIcon from '@mui/icons-material/DangerousOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import type { NextAction, StatusLight } from '../../types';

export interface StatusConfig {
  label: string;
  icon: typeof CheckCircleOutlinedIcon;
  guidance: string;
  palette: 'success' | 'warning' | 'error';
}

/**
 * Colorblind-friendly status icons — each uses a distinct shape + symbol:
 *   GREEN  → Circle + checkmark
 *   YELLOW → Triangle + exclamation
 *   RED    → Octagon + X
 */
export const STATUS_CONFIG: Record<StatusLight, StatusConfig> = {
  GREEN: {
    label: 'Verified',
    icon: CheckCircleOutlinedIcon,
    guidance:
      'Everything looks good. Fields are still editable if corrections are needed.',
    palette: 'success',
  },
  YELLOW: {
    label: 'Needs Review',
    icon: WarningAmberOutlinedIcon,
    guidance:
      'Review the data below and verify accuracy before the visit.',
    palette: 'warning',
  },
  RED: {
    label: 'Issue Detected',
    icon: DangerousOutlinedIcon,
    guidance: 'This must be resolved before the visit.',
    palette: 'error',
  },
};

export interface NextActionOption {
  value: NextAction;
  label: string;
  description: string;
}

/**
 * User-friendly labels for known alert codes.
 * Prevents internal/vendor terminology from leaking into the UI.
 */
const ALERT_LABEL_MAP: Record<string, string> = {
  // Demographics
  NIRVANA_DEMOGRAPHICS_MISMATCH: 'Patient info mismatch with payer',
  DEMOGRAPHICS_MISMATCH: 'Patient info mismatch with payer',
  // Coverage / discovery
  NO_COVERAGE: 'No insurance found',
  COVERAGE_TERMINATED: 'Coverage terminated',
  NO_ACTIVE_PLAN: 'No active plan',
  INACTIVE_PLAN: 'Inactive insurance plan',
  OUT_OF_NETWORK: 'Out of network',
  // Cost / benefits
  UNKNOWN_COST_SHARE: 'Unknown cost share',
  HIGH_MEMBER_OBLIGATION: 'High member obligation',
  // Authorization
  PRIOR_AUTH_REQUIRED: 'Prior authorization required',
  // Limits
  SESSION_LIMIT_WARNING: 'Session limit approaching',
  // Telehealth
  TELEHEALTH_COST_SHARE_DIFFERENCES: 'Telehealth cost share differs',
  // Payer issues
  PAYER_ERROR: 'Payer verification error',
  // Configuration
  NO_BILLING_NPI: 'Billing NPI not configured',
  PATIENT_NOT_FOUND: 'Patient not found',
};

/**
 * Actionable guidance for each alert code — tells the provider what to do.
 */
const ALERT_GUIDANCE_MAP: Record<string, string> = {
  // Demographics
  NIRVANA_DEMOGRAPHICS_MISMATCH:
    'The patient name or DOB on file doesn\'t match what the payer has. Review the demographics below, correct any errors, and re-run the check.',
  DEMOGRAPHICS_MISMATCH:
    'The patient name or DOB on file doesn\'t match what the payer has. Review the demographics below, correct any errors, and re-run the check.',
  // Coverage / discovery
  NO_COVERAGE:
    'No insurance was found for this patient. Add coverage manually or verify that demographics (name, DOB, zip) are complete and re-run.',
  COVERAGE_TERMINATED:
    'This patient\'s coverage has been terminated. Confirm with the patient whether they have new insurance or will self-pay.',
  NO_ACTIVE_PLAN:
    'No active plan was found for this patient. Ask the patient to verify their insurance information and member ID.',
  INACTIVE_PLAN:
    'The patient\'s insurance plan is inactive. Confirm with the patient whether they have new coverage.',
  OUT_OF_NETWORK:
    'This provider is out of network for the patient\'s plan. The patient may face higher costs or need a referral.',
  // Cost / benefits
  UNKNOWN_COST_SHARE:
    'The payer did not return cost-sharing details. You may need to check the payer portal or call to confirm copay/coinsurance.',
  HIGH_MEMBER_OBLIGATION:
    'The estimated patient cost is higher than typical. Discuss expected costs with the patient before the visit.',
  // Authorization
  PRIOR_AUTH_REQUIRED:
    'This service requires prior authorization. Submit an auth request before the appointment to avoid claim denial.',
  // Limits
  SESSION_LIMIT_WARNING:
    'The patient is approaching their session limit for this benefit period. Verify remaining sessions with the payer.',
  // Telehealth
  TELEHEALTH_COST_SHARE_DIFFERENCES:
    'The telehealth cost share may differ from in-person visits. Review the coverage details below to confirm the patient\'s copay and coinsurance for telehealth services.',
  // Payer issues
  PAYER_ERROR:
    'The payer returned an error during verification. Try again later, or verify coverage through the payer portal or by phone.',
  // Configuration
  NO_BILLING_NPI:
    'A billing NPI is required to check eligibility. Configure one in Settings \u2192 Billing.',
  PATIENT_NOT_FOUND:
    'The patient could not be found. Verify the patient record exists.',
};

/**
 * Returns actionable guidance for an alert code.
 * Falls back to a generic message using the human-readable label so unknown
 * codes still get useful guidance in the UI.
 */
export function getAlertGuidance(code: string): string {
  const upper = code.toUpperCase().replace(/\s+/g, '_');
  if (ALERT_GUIDANCE_MAP[upper]) return ALERT_GUIDANCE_MAP[upper];
  const label = formatAlertLabel(code).toLowerCase();
  return `The payer flagged "${label}" for this patient. Review the eligibility details below to confirm coverage before the visit.`;
}

/**
 * Converts alert codes to readable labels.
 * Uses known mapping first, falls back to SCREAMING_SNAKE_CASE → title case.
 */
export function formatAlertLabel(code: string): string {
  const upper = code.toUpperCase().replace(/\s+/g, '_');
  if (ALERT_LABEL_MAP[upper]) return ALERT_LABEL_MAP[upper];

  return code
    .split('_')
    .filter((word) => word.toLowerCase() !== 'nirvana')
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(' ');
}

export const NEXT_ACTION_OPTIONS: NextActionOption[] = [
  {
    value: 'none',
    label: 'None',
    description: 'No action needed — eligibility is clear.',
  },
  {
    value: 'portal_review',
    label: 'Portal Review',
    description: 'Check the payer portal for details.',
  },
  {
    value: 'phone_verify',
    label: 'Phone Verify',
    description: 'Call the payer to confirm coverage.',
  },
  {
    value: 'block',
    label: 'Block',
    description: 'Do not proceed until resolved.',
  },
];
